/// <amd-module name="CodeCoverage/CodeCoverageTab/Actions/CodeCoverageTabActionCreator" />
import { CodeCoverageTabActionHub, IBuildCodeCoverageDetails, ICodeCoverageModuleDetails, ICodeCoverageSummaryViewProps, IConfigCodeCoverageDetails, IFolderViewBuildCodeCoverageDetails, IFolderViewCoverageDetails } from 'Actions/CodeCoverageTabActionHub';
import { BuildArtifact } from 'Build/Client/Contracts/Build';
import * as Resources from 'CodeCoverage/CodeCoverageTab/Resources';
import { PerfScenarios, TelemetryEvents} from 'CodeCoverage/Common/Constants';
import { RunViewArtifact } from 'CodeCoverage/Common/RunViewArtifact';
import { CodeCoverageSource } from 'CodeCoverage/Common/Sources/CodeCoverageSource';
import { BuildConfiguration, BuildCoverage, SourceViewBuildCoverage } from 'Test/common-util/Contracts/Test';
import { getTFSPageData, IProjectData } from 'Tfs/Platform/Page';
import * as Utils_String from 'VSS/Core/Util/String';
import { IVssComponentContext } from 'VSS/Platform/Layout';
import { routeUrl } from 'VSS/Platform/Location';
import { IVssPerformanceService } from 'VSS/Platform/Performance';
import { IRunExternalService } from "Build/Run/RunExternalService";
import { CodeCoverageMessagesMap } from 'CodeCoverage/CodeCoverageTab/Stores/CodeCoverageMessagesMap';
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";

export class CodeCoverageTabActionCreator {
    private _perfService: IVssPerformanceService;
    private externalService: IRunExternalService;
    private coverageDetailedStatus: string;
    private _telemetryService: IVssTelemetryService;

    private constructor(private pageContext: IVssComponentContext) {
        this._actions = CodeCoverageTabActionHub.getInstance();
        this._source = CodeCoverageSource.getInstance(pageContext);
        this._perfService = this.pageContext.pageContext.getService<IVssPerformanceService>("IVssPerformanceService");
        this.externalService = this.pageContext.pageContext.getService<IRunExternalService>("IRunExternalService");
        this._telemetryService = this.pageContext.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
    }

    public static getInstance(pageContext: IVssComponentContext) {
        if (!CodeCoverageTabActionCreator._instance) {
            CodeCoverageTabActionCreator._instance = new CodeCoverageTabActionCreator(pageContext);
        }

        return CodeCoverageTabActionCreator._instance;
    }

//#region API Fetch

    public updateModuleFilter(value: string): void {
        this._actions.updateModuleFilterText.invoke(value);
    }

    public updateBuildConfigSelection(value: number): void {
        this._actions.updateBuildConfigSelection.invoke(value);
    }

    public updateEnableFolderViewState(value: boolean): void {
        this._actions.updateEnableFolderViewState.invoke(value);
    }

    public fetchBuildCodeCoverage(runViewArtifact: RunViewArtifact): void {
        this._perfService.startScenario(PerfScenarios.APICall, /* isPageInteractive */ false, /* startTime */ undefined, { "Call": "getBuildCodeCoverage" });

        this._source.getBuildCodeCoverage(runViewArtifact).then((buildCoverage) => {
           if (buildCoverage && buildCoverage.length) {
                this._actions.updateBuildCodeCoverage.invoke(this._getCodeCoverageDetails(buildCoverage));
           }
           else {
                this.fetchCodeCoverageHTMLSummary(runViewArtifact);          
            }
              
            this._perfService.endScenario(PerfScenarios.Area, PerfScenarios.APICall, /* isPageInteractive */ false, /* startTime */ undefined,  { "Call": "getBuildCodeCoverage" });
        });
    }

    public fetchFolderViewBuildCodeCoverage(runViewArtifact: RunViewArtifact): void {
        this._source.getFolderViewBuildCodeCoverage(runViewArtifact).then((folderViewBuildCodeCoverage) => {
            if (folderViewBuildCodeCoverage && folderViewBuildCodeCoverage.length) {
                this._actions.updateFolderViewBuildCodeCoverage.invoke(this._getFolderViewCodeCoverageDetails(folderViewBuildCodeCoverage));
            }
        });
    }

    public fetchGetCodecoverageSummary(runViewArtifact: RunViewArtifact): void {
        this._perfService.startScenario(PerfScenarios.APICall, /* isPageInteractive */ false, /* startTime */ undefined, { "Call": "getCodeCoverageSummary" });

        this.externalService.getCodeCoverageSummary(runViewArtifact.buildId, -10).then((codeCoverageSummary) => {
            if (codeCoverageSummary) {
                this.coverageDetailedStatus = codeCoverageSummary.coverageDetailedSummaryStatus;
            }
            this._perfService.endScenario(PerfScenarios.Area, PerfScenarios.APICall, /* isPageInteractive */ false, /* startTime */ undefined, { "Call": "getCodeCoverageSummary" });
            this.fetchCoverageFailureMessages();
        });
        
    }

    public fetchCoverageFailureMessages(): void {
        if (this.coverageDetailedStatus != "") {
            try {
                let failureMessage = CodeCoverageMessagesMap.codeCoverageFailureMap.get(this.coverageDetailedStatus);
                if (failureMessage) {
                    this._actions.updateCodeCoverageFailureMessage.invoke(failureMessage);
                }
                else {
                    this._actions.updateNoCodeCoverageMessage.invoke(Resources.CodeCoverageInProgress);
                }
            }
            catch (exception)
            {
                this._telemetryService.publishEvent(TelemetryEvents.Area, TelemetryEvents.CodeCoverageFailureMessage, { error: exception });
                throw new Error(exception);
            }
        }
    }

    public fetchCodeCoverageHTMLSummary(runViewArtifact: RunViewArtifact) {
        this._perfService.startScenario(PerfScenarios.APICall, /* isPageInteractive */ false, /* startTime */ undefined, { "Call": "getBuildArtifacts" });
        this._source.getBuildArtifacts(runViewArtifact).then((pipelineArtifacts) => {
            if (pipelineArtifacts && pipelineArtifacts.length > 0) {
                const url = this._getHTMLSummaryLinkFromArtifact(pipelineArtifacts, runViewArtifact.buildId);
                if (url) {
                    this._actions.updateCodeCoverageSummary.invoke(url as string);
                }
                else {
                    this._actions.updateNoCodeCoverageMessage.invoke(Resources.NoBuildArtifactsFound);
                }
            }
            else if (isFeatureFlagEnabled(this.pageContext.pageContext, "WebAccess.TestManagement.CodeCoverage.EnableTabByDefault", false)) {
                this.fetchGetCodecoverageSummary(runViewArtifact);
            }
            else {
                this._actions.updateNoCodeCoverageMessage.invoke(Resources.BuildDetailsSummaryNoCodeCoverageNoLink);
            }   
            this._perfService.endScenario(PerfScenarios.Area, PerfScenarios.APICall, /* isPageInteractive */ false, /* startTime */ undefined,  { "Call": "getBuildArtifacts" });
        })
    }

//#endregion

//#region Code Coverage Data

    private _getHTMLSummaryLinkFromArtifact(buildArtifacts: BuildArtifact[], buildId: number): string | null {

        let filteredArtifacts = buildArtifacts.filter((pipelineArtifact: BuildArtifact) => {
            // for now the uploaded coverage artifact name is called "CodeCoverageReport", the artifact download link will 
            // ultimately be needed to be provided by the CodeCoverageSummary object                        
            return (Utils_String.equals(pipelineArtifact.name, Utils_String.format("Code Coverage Report_{0}", buildId)));
        });

        if (filteredArtifacts.length >= 1 
            && filteredArtifacts[0].resource
            && filteredArtifacts[0].resource._links
            && filteredArtifacts[0].resource._links.web
            && filteredArtifacts[0].resource._links.web.href
            && filteredArtifacts[0].resource._links.web.href.length
            && filteredArtifacts[0].resource.data
            && filteredArtifacts[0].resource.data.length) {

            const tfsContext = getTFSPageData(this.pageContext.pageContext);

            if(tfsContext) {
                
                let collectionUrl = this._getCollectionUrl();
                let projectId = (tfsContext.project as IProjectData).id;
                let containerPath = filteredArtifacts[0].resource.data;

                // Data format will be in this format (#/ContainerId/FolderPath)
                // Double check if that's the format and if not take precautions
                if (containerPath.charAt(0) === "#") {
                    containerPath = containerPath.substring(1);
                }
                if (containerPath.charAt(0) !== "/") {
                    containerPath = "/" + containerPath;
                }

                // index.html is the one that should be retrieved
                // preferred to hardcode at client rather than at server
                let url = collectionUrl.lastIndexOf("/") === collectionUrl.length - 1 ? collectionUrl : collectionUrl  + "/"; 
                url = url + projectId + "/_apis/test/CodeCoverage/browse" + containerPath + "/index.html";
                
                return url;
            }
            else {
                console.log(Resources.NoBuildArtifactsFound);
            }
        }
        else {
            console.log(Resources.NoBuildArtifactsFound);
        }
    
        return null;
    }

    private _getFolderViewCodeCoverageDetails(folderViewCodeCoverageData: SourceViewBuildCoverage[]): IFolderViewBuildCodeCoverageDetails {

        const folderViewCoverages = folderViewCodeCoverageData.map(folderViewCoverageData => {
            const linesCovered = folderViewCoverageData.folderCoverageData.coverageStatistics.linesCovered | 0;
            const linesNotCovered = folderViewCoverageData.folderCoverageData.coverageStatistics.linesNotCovered | 0;
            const linesPartiallyCovered = folderViewCoverageData.folderCoverageData.coverageStatistics.linesPartiallyCovered | 0;
            const totalLines = linesCovered + linesNotCovered + linesPartiallyCovered;
            const summaryView: ICodeCoverageSummaryViewProps = {
                moduleCount: 0,
                totalLines,
                totalCoveredLines: linesCovered,
                coveragePercent: Utils_String.localeFormat("{0}%", (totalLines ? ((linesCovered / totalLines) * 100) : 0).toFixed(2))
            };
            return {
                folderCoverageData: folderViewCoverageData.folderCoverageData,
                summaryViewProps: summaryView
            } as IFolderViewCoverageDetails

        })
        return {
            folderViewCoverages: folderViewCoverages,
            buildConfigurations: folderViewCodeCoverageData.map((folderViewCoverageData: SourceViewBuildCoverage) => { return folderViewCoverageData.configuration; })
        };
    }

    private _getCodeCoverageDetails(codeCoverageData: BuildCoverage[]): IBuildCodeCoverageDetails {

        const buildCoverages = codeCoverageData.map(coverageData => {
                
            const codeCoverageModuleList: ICodeCoverageModuleDetails[] = [];
            let totalLines = 0;
            let totalCoveredLines = 0;

            coverageData.modules.forEach(coverageModule => {
                const linesCovered = coverageModule.statistics.linesCovered | 0;
                const linesNotCovered = coverageModule.statistics.linesNotCovered | 0;
                const linesPartiallyCovered = coverageModule.statistics.linesPartiallyCovered | 0;
                
                const linesInModule = (linesCovered + linesNotCovered + linesPartiallyCovered);
                totalLines = totalLines + linesInModule;
                totalCoveredLines = totalCoveredLines + linesCovered;
                
                codeCoverageModuleList.push({
                    moduleName: coverageModule.name,
                    moduleUrl: coverageModule.fileUrl, 
                    coveredLines: linesCovered, 
                    totalLines: linesInModule,
                    lineCoverage: Utils_String.localeFormat("{0}%", (linesInModule ? ((linesCovered / linesInModule) * 100) : 0).toFixed(2)),
                } as ICodeCoverageModuleDetails);
            });

            return {
                codeCoverageModuleList,
                summaryViewProps: {
                    moduleCount: coverageData.modules.length,
                    totalLines,
                    totalCoveredLines,
                    coveragePercent: Utils_String.localeFormat("{0}%", (totalLines ? ((totalCoveredLines / totalLines) * 100) : 0).toFixed(2))
                } as ICodeCoverageSummaryViewProps,
                coverageDownloadLink: coverageData.codeCoverageFileUrl || "",
            } as IConfigCodeCoverageDetails;
            
        });

        return {
            buildCoverages: buildCoverages,
            buildConfigurations: this._getBuildConfigurations(codeCoverageData)
        };
    }
    
    private _getBuildConfigurations(codeCoverageData: BuildCoverage[]): BuildConfiguration[] {
        return codeCoverageData.map((coverageData: BuildCoverage) => { return coverageData.configuration; });
    }

    private _getCollectionUrl(): string {
        return routeUrl(this.pageContext.pageContext, "ms.vss-tfs-web.project-overview-route");
    }

//#endregion

    private static _instance: CodeCoverageTabActionCreator;
    private _actions: CodeCoverageTabActionHub;
    private _source: CodeCoverageSource;
    public codeCoverageFailureMessage: string;
}

